import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Upload, FileSpreadsheet, FileText, FileCode2, ReceiptText,
  Plus, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronRight,
  Trash2, Building2, Save, ListChecks, Wallet, Landmark, TrendingUp, Percent,
  Pencil, ClipboardCheck, Send, ArrowRight, Undo2,
} from "lucide-react";

const peppolFromTrn = (trn) => {
  const d = (trn || "").replace(/\D/g, "");
  return d.length >= 10 ? `0235:${d.slice(0, 10)}` : "";
};
const trnDigits = (trn) => (trn || "").replace(/\D/g, "");

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const IPC_TYPES = [
  { value: "advance", label: "Advance Payment" },
  { value: "progress", label: "Progress" },
  { value: "final", label: "Final" },
  { value: "retention_release", label: "Retention Release" },
];
const typeLabel = (v) => (IPC_TYPES.find((t) => t.value === v) || IPC_TYPES[1]).label;

const BOQ_UNITS = ["Lump Sum", "No.", "m", "m²", "m³", "mm", "cm", "km", "kg", "ton", "L", "Set", "Pair", "Box", "Roll", "Bag", "Hour", "Day", "Month", "%"];

function ReviewBanner({ rv, onFixBuyer }) {
  if (!rv) return null;
  const blocked = rv.status === "blocked";
  const fixLink = (w) => {
    const lw = w.toLowerCase();
    if (lw.startsWith("supplier"))
      return <a href="/settings" data-testid="fix-supplier-link" className="underline font-semibold hover:text-foreground">Go to Company profile →</a>;
    if (lw.startsWith("buyer") || lw.includes("emirate"))
      return <button type="button" data-testid="fix-buyer-link" onClick={onFixBuyer} className="underline font-semibold hover:text-foreground">Edit contract details →</button>;
    return null;
  };
  return (
    <div data-testid="review-banner" className={`rounded-xl border p-3 ${blocked ? "border-rose-300 bg-rose-50 dark:bg-rose-950/30" : "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30"}`}>
      <div className={`flex items-center justify-between gap-2 font-bold text-sm ${blocked ? "text-rose-700 dark:text-rose-300" : "text-emerald-700 dark:text-emerald-300"}`}>
        <span className="flex items-center gap-2">{blocked ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
          {blocked ? "Fix the issues below" : "Ready to generate"}</span>
        <span>Net payable AED {money(rv.total)}</span>
      </div>
      {!!(rv.lines && rv.lines.length) && (
        <div className="text-xs text-muted-foreground mt-1">
          Gross certified to-date AED {money(rv.gross_todate)} · Less previous AED {money(rv.prev_todate)} · Work this IPC AED {money(rv.work_period)}
        </div>
      )}
      {!!(rv.extra && rv.extra.length) && (
        <div className="mt-1.5 space-y-0.5">
          {rv.extra.map((e, i) => (
            <div key={i} className="flex justify-between text-xs text-slate-700 dark:text-slate-300">
              <span>{e.description}{e.taxable === false ? " (non-taxable)" : ""}</span>
              <span className="font-mono">AED {money(e.amount)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="text-xs text-muted-foreground mt-1">Taxable AED {money(rv.subtotal)} · VAT AED {money(rv.vat_amount)}{rv.nontax_total ? ` · Non-taxable AED ${money(rv.nontax_total)}` : ""}</div>
      {rv.errors?.map((e, i) => <div key={`e${i}`} className="flex gap-1.5 text-xs text-rose-700 mt-1"><XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {e}</div>)}
      {rv.warnings?.map((w, i) => (
        <div key={`w${i}`} className="flex flex-wrap gap-1.5 text-xs text-amber-700 mt-1">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span>{w}</span> {fixLink(w)}
        </div>
      ))}
    </div>
  );
}

function IpcCard({ ipc, contract, boq, onChanged, onDelete, fullService, onFixBuyer, onViewClearance, onCreditNote }) {
  const [open, setOpen] = useState(!ipc.invoice);
  const [busy, setBusy] = useState("");
  const [jam, setJam] = useState(null);
  const [jamBusy, setJamBusy] = useState(false);
  useEffect(() => {
    if (ipc.invoice) api.get(`/einvoice/submissions/ipc/${ipc.id}`).then((r) => setJam(r.data.submission)).catch(() => {});
  }, [ipc.id, ipc.invoice]);
  const submitJam = async () => {
    setJamBusy(true);
    try {
      const r = await api.post(`/einvoice/ipcs/${ipc.id}/submit-wafeq`);
      setJam(r.data); toast.success("Submitted to the FTA via Wafeq — clearance in progress");
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const s = await api.get(`/einvoice/submissions/ipc/${ipc.id}`);
          if (s.data.submission) {
            setJam(s.data.submission);
            const st = s.data.submission.status;
            if (["APPROVED", "REPORTED", "DELIVERED", "REJECTED", "INVALID", "NOT_COMPLIANT", "FAILED", "ACKNOWLEDGED", "ERROR"].includes(st)) return;
          }
        } catch { /* ignore */ }
        if (tries < 8) setTimeout(poll, 4000);
      };
      setTimeout(poll, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit to the FTA");
      try { const s = await api.get(`/einvoice/submissions/ipc/${ipc.id}`); if (s.data.submission) setJam(s.data.submission); } catch { /* ignore */ }
    }
    finally { setJamBusy(false); }
  };
  const [ipcType, setIpcType] = useState(ipc.ipc_type || "progress");
  const [desc, setDesc] = useState(ipc.ipc_description || "");
  const [period, setPeriod] = useState(ipc.period_date || "");
  const [issue, setIssue] = useState(ipc.issue_date || "");
  const [retPct, setRetPct] = useState(ipc.retention_pct ?? contract.retention_pct ?? 10);
  const [advRecPct, setAdvRecPct] = useState(ipc.advance_recovery_pct ?? contract.advance_pct ?? 0);
  const [applyRet, setApplyRet] = useState(ipc.apply_retention !== false);
  const [applyAdvRec, setApplyAdvRec] = useState(ipc.apply_advance_recovery !== false);
  const [advAmount, setAdvAmount] = useState(ipc.advance_amount ?? "");
  const [relAmount, setRelAmount] = useState(ipc.retention_release_amount ?? "");
  const [progress, setProgress] = useState(() =>
    boq.map((b) => {
      const p = (ipc.progress || []).find((x) => String(x.line_no) === String(b.line_no));
      return { line_no: String(b.line_no), cum_pct: p ? p.cum_pct : 0 };
    }));
  const [extras, setExtras] = useState(ipc.extras || []);
  const [rv, setRv] = useState(ipc.review);
  const upRef = useRef();

  const isProgress = ipcType === "progress" || ipcType === "final";

  const payload = () => ({
    ipc_number: ipc.ipc_number, ipc_type: ipcType, ipc_description: desc,
    period_date: period, issue_date: issue,
    progress, extras,
    retention_pct: retPct === "" ? null : Number(retPct),
    advance_recovery_pct: advRecPct === "" ? null : Number(advRecPct),
    advance_amount: advAmount === "" ? null : Number(advAmount),
    retention_release_amount: relAmount === "" ? null : Number(relAmount),
    apply_retention: applyRet, apply_advance_recovery: applyAdvRec,
  });

  const save = async () => {
    const r = await api.put(`/einvoice/ipcs/${ipc.id}`, payload());
    if (r.data?.review) setRv(r.data.review);
    return r.data;
  };

  const doSave = async () => {
    setBusy("save");
    try { await save(); toast.success("IPC saved"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(""); }
  };

  const runReview = async () => {
    setBusy("review");
    try { await save(); const r = await api.post(`/einvoice/ipcs/${ipc.id}/review`); setRv(r.data); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Review failed"); }
    finally { setBusy(""); }
  };

  const generate = async () => {
    setBusy("gen");
    try {
      await save();
      const r = await api.post(`/einvoice/ipcs/${ipc.id}/generate`);
      if (r.data.validation?.warnings?.length) toast.warning(`Generated with ${r.data.validation.warnings.length} warning(s)`);
      else toast.success("E-invoice generated");
      onChanged();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Could not generate"));
      if (d?.errors || d?.warnings) setRv({ status: "blocked", errors: d.errors || [], warnings: d.warnings || [], subtotal: 0, vat_amount: 0, total: 0 });
    } finally { setBusy(""); }
  };

  const uploadIpc = async (file) => {
    if (!file) return;
    setBusy("upload");
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/einvoice/ipcs/${ipc.id}/ipc-upload`, fd);
      setProgress(boq.map((b) => {
        const p = r.data.progress.find((x) => String(x.line_no) === String(b.line_no));
        return { line_no: String(b.line_no), cum_pct: p ? p.cum_pct : 0 };
      }));
      setRv(r.data.review);
      const m = r.data.meta || {};
      if (m.ipc_number || m.period_date || m.issue_date) {
        toast.message("Read from file", { description: `IPC ${m.ipc_number || "—"} · period ${m.period_date || "—"} · issued ${m.issue_date || "—"}` });
      }
      toast.success("IPC read — review and edit the % below");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(""); if (upRef.current) upRef.current.value = ""; }
  };

  const setPct = (no, v) => setProgress(progress.map((p) => (p.line_no === String(no) ? { ...p, cum_pct: v } : p)));
  const addExtra = () => setExtras([...extras, { kind: "custom", description: "", amount: 0, taxable: true }]);
  const setExtra = (i, k, v) => setExtras(extras.map((e, idx) => (idx === i ? { ...e, [k]: v } : e)));
  const delExtra = (i) => setExtras(extras.filter((_, idx) => idx !== i));

  const inv = ipc.invoice;
  const scopeOf = (no) => Number((boq.find((b) => String(b.line_no) === String(no)) || {}).scope || 0);
  const pctOf = (no) => (progress.find((p) => p.line_no === String(no)) || {}).cum_pct ?? 0;

  return (
    <div data-testid={`ipc-card-${ipc.id}`} className="rounded-2xl border border-border bg-card overflow-hidden">
      <button data-testid={`ipc-toggle-${ipc.id}`} onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-secondary/40">
        <div className="flex items-center gap-3 min-w-0">
          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          <div className="min-w-0">
            <div className="font-bold text-slate-900 dark:text-white truncate flex items-center gap-2">
              {ipc.ipc_number || "IPC"}
              <span className="text-[10px] font-semibold uppercase tracking-wide text-primary bg-primary/10 px-1.5 py-0.5 rounded">{typeLabel(ipc.ipc_type)}</span>
            </div>
            <div className="text-xs text-muted-foreground truncate">{ipc.period_date ? `Period ${ipc.period_date}` : "No period"}{ipc.issue_date ? ` · Issued ${ipc.issue_date}` : ""}{ipc.ipc_description ? ` · ${ipc.ipc_description}` : ""}</div>
          </div>
        </div>
        {inv
          ? <span data-testid={`ipc-invoiced-${ipc.id}`} className="text-[11px] font-bold text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded flex items-center gap-1 shrink-0"><ReceiptText className="w-3 h-3" /> {inv.invoice_number}</span>
          : <span className="text-[11px] font-bold text-amber-700 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 rounded shrink-0">Draft</span>}
      </button>

      {open && (
        <div className="p-4 pt-0 space-y-4 border-t border-border">
          {/* Certificate details */}
          <div className="pt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={ipcType} onValueChange={setIpcType}>
                <SelectTrigger className="h-9" data-testid={`ipc-type-${ipc.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{IPC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Period date</Label><Input type="date" className="h-9" data-testid={`ipc-period-${ipc.id}`} value={period} onChange={(e) => setPeriod(e.target.value)} /></div>
            <div><Label className="text-xs">Issue date</Label><Input type="date" className="h-9" data-testid={`ipc-issue-${ipc.id}`} value={issue} onChange={(e) => setIssue(e.target.value)} /></div>
            <div><Label className="text-xs">Description</Label><Input className="h-9" data-testid={`ipc-desc-${ipc.id}`} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="e.g. May valuation" /></div>
          </div>

          {/* Advance Payment certificate */}
          {ipcType === "advance" && (
            <div className="rounded-xl border border-border bg-secondary/30 p-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Advance payment</h4>
              <div className="flex items-center gap-2">
                <Label className="text-xs w-40">Advance amount (AED)</Label>
                <Input type="number" className="h-8 w-40 text-right" data-testid={`adv-amount-${ipc.id}`}
                       value={advAmount} placeholder={String(Math.round((contract.boq_value || 0) * (contract.advance_pct || 0) / 100))}
                       onChange={(e) => setAdvAmount(e.target.value)} />
                <span className="text-xs text-muted-foreground">default = {contract.advance_pct || 0}% of contract value. Recovered automatically in later Progress IPCs.</span>
              </div>
            </div>
          )}

          {/* Retention Release certificate */}
          {ipcType === "retention_release" && (
            <div className="rounded-xl border border-border bg-secondary/30 p-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Retention release</h4>
              <div className="flex items-center gap-2">
                <Label className="text-xs w-40">Amount to release (AED)</Label>
                <Input type="number" className="h-8 w-40 text-right" data-testid={`rel-amount-${ipc.id}`}
                       value={relAmount} onChange={(e) => setRelAmount(e.target.value)} />
                <span className="text-xs text-muted-foreground">leave blank to release all retention held to-date.</span>
              </div>
            </div>
          )}

          {/* Progress / Final certificate */}
          {isProgress && (
            <>
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Invoice line items — cumulative % complete to-date</h4>
                <input ref={upRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid={`ipc-upload-${ipc.id}`} onChange={(e) => uploadIpc(e.target.files[0])} />
                <Button variant="ghost" size="sm" className="gap-1.5 text-xs" disabled={busy === "upload"} onClick={() => upRef.current?.click()}>
                  {busy === "upload" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload IPC & auto-fill
                </Button>
              </div>

              <div className="overflow-x-auto border border-border rounded-xl">
                <table className="w-full text-xs">
                  <thead className="bg-accent text-[10px] uppercase tracking-wider text-muted-foreground">
                    <tr><th className="px-3 py-2 text-left font-bold">Line</th><th className="px-3 py-2 text-left font-bold">Description</th><th className="px-3 py-2 text-right font-bold">Scope (AED)</th><th className="px-3 py-2 text-right font-bold">% Complete</th><th className="px-3 py-2 text-right font-bold">Cumulative (AED)</th></tr>
                  </thead>
                  <tbody>
                    {boq.map((b) => (
                      <tr key={b.line_no} className="border-t border-border">
                        <td className="px-3 py-1.5 font-mono">{b.line_no}</td>
                        <td className="px-3 py-1.5">{b.description}</td>
                        <td className="px-3 py-1.5 text-right">{money(b.scope)}</td>
                        <td className="px-3 py-1.5 text-right">
                          <Input type="number" data-testid={`pct-${ipc.id}-${b.line_no}`} className="h-8 w-24 ml-auto text-right"
                                 value={pctOf(b.line_no)}
                                 onChange={(e) => setPct(b.line_no, parseFloat(e.target.value) || 0)} />
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono">{money(scopeOf(b.line_no) * (Number(pctOf(b.line_no)) || 0) / 100)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Auto advance recovery + retention */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div className={`rounded-xl border p-3 ${applyAdvRec ? "border-border" : "border-dashed border-border opacity-70"}`}>
                  <label className="flex items-center gap-2 text-xs font-bold text-slate-900 dark:text-white">
                    <input type="checkbox" data-testid={`apply-advrec-${ipc.id}`} checked={applyAdvRec} onChange={(e) => setApplyAdvRec(e.target.checked)} />
                    Auto advance recovery
                  </label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input type="number" className="h-8 w-20 text-right" data-testid={`advrec-pct-${ipc.id}`} value={advRecPct} onChange={(e) => setAdvRecPct(e.target.value)} />
                    <span className="text-xs text-muted-foreground">% of work this IPC. Applies only after an Advance Payment IPC is generated.</span>
                  </div>
                </div>
                <div className={`rounded-xl border p-3 ${applyRet ? "border-border" : "border-dashed border-border opacity-70"}`}>
                  <label className="flex items-center gap-2 text-xs font-bold text-slate-900 dark:text-white">
                    <input type="checkbox" data-testid={`apply-ret-${ipc.id}`} checked={applyRet} onChange={(e) => setApplyRet(e.target.checked)} />
                    Auto retention
                  </label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input type="number" className="h-8 w-20 text-right" data-testid={`ret-pct-${ipc.id}`} value={retPct} onChange={(e) => setRetPct(e.target.value)} />
                    <span className="text-xs text-muted-foreground">% held on work this IPC (default = contract retention).</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Custom adjustments */}
          <div>
            <div className="flex items-center justify-between gap-2 mb-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Other adjustments</h4>
              <Button variant="outline" size="sm" className="text-xs h-7" data-testid={`add-item-${ipc.id}`} onClick={addExtra}>+ Item</Button>
            </div>
            {extras.length === 0 ? <p className="text-xs text-muted-foreground">Advance recovery and retention are applied automatically above. Add any other one-off adjustment here.</p> : (
              <div className="space-y-2">
                {extras.map((e, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input className="h-8 flex-1" placeholder="Description" value={e.description} onChange={(ev) => setExtra(i, "description", ev.target.value)} />
                    <Input type="number" className="h-8 w-32 text-right" placeholder="Amount" value={e.amount} onChange={(ev) => setExtra(i, "amount", parseFloat(ev.target.value) || 0)} />
                    <label className="flex items-center gap-1 text-[11px] text-muted-foreground"><input type="checkbox" checked={e.taxable !== false} onChange={(ev) => setExtra(i, "taxable", ev.target.checked)} /> VAT</label>
                    <button onClick={() => delExtra(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {rv && <ReviewBanner rv={rv} onFixBuyer={onFixBuyer} />}

          <div className="flex flex-wrap gap-2 items-center">
            <Button variant="ghost" size="sm" className="gap-1.5" data-testid={`save-${ipc.id}`} disabled={busy === "save"} onClick={doSave}>
              {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </Button>
            <Button variant="outline" size="sm" data-testid={`review-${ipc.id}`} className="gap-1.5" disabled={busy === "review"} onClick={runReview}>
              {busy === "review" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListChecks className="w-4 h-4" />} Run review
            </Button>
            <Button size="sm" data-testid={`generate-${ipc.id}`} className="gap-1.5" disabled={busy === "gen" || (rv && rv.status === "blocked")} onClick={generate}>
              {busy === "gen" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ReceiptText className="w-4 h-4" />} {inv ? "Regenerate invoice" : "Generate invoice"}
            </Button>
            <button data-testid={`delete-ipc-${ipc.id}`} onClick={() => onDelete(ipc.id)} className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1 ml-auto"><Trash2 className="w-3 h-3" /> Delete IPC</button>
          </div>

          {inv && (
            <div data-testid={`invoice-summary-${ipc.id}`} className="rounded-xl border border-border bg-secondary/30 p-4">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <span>Taxable <b>AED {money(inv.subtotal)}</b></span>
                <span>VAT <b>AED {money(inv.vat_amount)}</b></span>
                <span>Net payable <b className="text-primary">AED {money(inv.total)}</b></span>
                <span className="text-xs text-muted-foreground self-center">You can edit above and regenerate — the invoice number stays the same.</span>
              </div>
              {inv.amount_in_words && <div data-testid={`inv-words-${ipc.id}`} className="text-xs text-slate-700 dark:text-slate-300 mt-1 font-medium">Amount in words: {inv.amount_in_words}</div>}
              <div className="flex flex-wrap gap-2 mt-3">
                {[["xlsx", "Excel", FileSpreadsheet], ["csv", "CSV", FileSpreadsheet], ["pdf", "PDF", FileText], ["xml", "UBL XML", FileCode2]].map(([fmt, label, Ic]) => (
                  <Button key={fmt} variant="outline" size="sm" data-testid={`dl-${fmt}-${ipc.id}`} className="gap-1.5"
                          onClick={() => downloadFile(`/einvoice/ipcs/${ipc.id}/download?fmt=${fmt}`, `${inv.invoice_number}.${fmt}`)}>
                    <Ic className="w-3.5 h-3.5" /> {label}
                  </Button>
                ))}
                <Button variant="outline" size="sm" data-testid={`ipc-credit-${ipc.id}`} className="gap-1.5 border-rose-300 text-rose-600 dark:text-rose-400"
                        onClick={() => onCreditNote(ipc.id)}>
                  <Undo2 className="w-3.5 h-3.5" /> Credit note
                </Button>
              </div>
              {(fullService || jam) && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border" data-testid={`ipc-jam-${ipc.id}`}>
                  <span className="text-xs font-semibold text-muted-foreground">Approved ASP:</span>
                  {jam && jam.status !== "ERROR" ? (
                    <>
                      <span data-testid={`ipc-jam-status-${ipc.id}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${["APPROVED", "REPORTED", "DELIVERED"].includes(jam.status) ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : jam.status === "RECEIVED" || jam.status === "SENT" ? "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" : "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300"}`}>
                        {jam.status === "RECEIVED" && <Loader2 className="w-3 h-3 animate-spin" />}{["APPROVED", "REPORTED", "DELIVERED"].includes(jam.status) && <CheckCircle2 className="w-3 h-3" />}{jam.status_label || jam.status}
                      </span>
                      <span data-testid={`ipc-jam-mode-${ipc.id}`} className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${jam.mode === "live" ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300" : "bg-accent text-muted-foreground"}`}>
                        {jam.mode_label || (jam.mode === "live" ? "Live · Approved ASP" : "Simulation")}
                      </span>
                      <button type="button" data-testid={`ipc-jam-clearance-${ipc.id}`} onClick={onViewClearance} className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline inline-flex items-center gap-1">View in ASP clearance <ArrowRight className="w-3 h-3" /></button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" data-testid={`ipc-jam-submit-${ipc.id}`} className="gap-1.5 h-7 text-xs border-indigo-300 text-indigo-700 dark:text-indigo-300" disabled={jamBusy} onClick={submitJam}>
                        {jamBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {jam && jam.status === "ERROR" ? "Retry" : "Submit to FTA"}
                      </Button>
                      {jam && jam.status === "ERROR" && <span className="text-[10px] text-red-600 dark:text-red-400">Failed — check the ASP connection</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EInvoiceContract() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [ipcs, setIpcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [boq, setBoq] = useState([]);
  const [open, setOpen] = useState(false);
  const [ipcForm, setIpcForm] = useState({ ipc_number: "", ipc_type: "progress", ipc_description: "", period_date: "", issue_date: "" });
  const [summary, setSummary] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [finalOpen, setFinalOpen] = useState(false);
  const [voOpen, setVoOpen] = useState(false);
  const [voEditId, setVoEditId] = useState(null);
  const [voForm, setVoForm] = useState({ line_no: "", ref: "", description: "", amount: 0, date: "", approved: true });
  const [fullService, setFullService] = useState(false);
  useEffect(() => { api.get("/einvoice/subscription").then((r) => setFullService(r.data.plan === "full_service" || r.data.is_admin)).catch(() => {}); }, []);
  const upRef = useRef();
  const variations = contract?.variations || [];

  const openVo = (vo) => {
    if (vo) {
      setVoEditId(vo.id);
      setVoForm({ line_no: String(vo.line_no), ref: vo.ref || "", description: vo.description || "", amount: vo.amount || 0, date: vo.date || "", approved: vo.approved !== false });
    } else {
      setVoEditId(null);
      setVoForm({ line_no: boq[0] ? String(boq[0].line_no) : "", ref: "", description: "", amount: 0, date: "", approved: true });
    }
    setVoOpen(true);
  };
  const saveVo = async () => {
    if (!voForm.line_no) { toast.error("Pick a BOQ line"); return; }
    setBusy("vo");
    try {
      const payload = { ...voForm, line_no: parseInt(voForm.line_no, 10), amount: parseFloat(voForm.amount) || 0 };
      const r = voEditId
        ? await api.put(`/einvoice/contracts/${id}/variations/${voEditId}`, payload)
        : await api.post(`/einvoice/contracts/${id}/variations`, payload);
      setContract({ ...contract, ...r.data, boq });
      setVoOpen(false); toast.success(voEditId ? "Variation updated" : "Variation added"); loadSummary();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save variation"); }
    finally { setBusy(""); }
  };
  const delVo = async (vid) => {
    try { const r = await api.delete(`/einvoice/contracts/${id}/variations/${vid}`); setContract({ ...contract, ...r.data, boq }); toast.success("Variation removed"); loadSummary(); }
    catch { toast.error("Could not remove variation"); }
  };
  const toggleVo = async (vo) => {
    try {
      const r = await api.put(`/einvoice/contracts/${id}/variations/${vo.id}`, {
        line_no: parseInt(vo.line_no, 10), ref: vo.ref, description: vo.description, amount: vo.amount, date: vo.date, approved: !(vo.approved !== false),
      });
      setContract({ ...contract, ...r.data, boq }); loadSummary();
    } catch { toast.error("Could not update variation"); }
  };

  const openEdit = (v) => {
    setEditOpen(v);
    if (v && contract) setEditForm({
      title: contract.title || "", project_name: contract.project_name || "",
      contract_ref: contract.contract_ref || "", contract_date: contract.contract_date || "",
      retention_pct: contract.retention_pct ?? 10, advance_pct: contract.advance_pct ?? 0,
      buyer: {
        company_name: contract.buyer?.company_name || "", trn: contract.buyer?.trn || "",
        city: contract.buyer?.city || "", address: contract.buyer?.address || "",
        peppol_id: contract.buyer?.peppol_id || "",
      },
    });
  };
  const saveContract = async () => {
    setBusy("edit");
    try {
      const r = await api.put(`/einvoice/contracts/${id}`, editForm);
      setContract({ ...r.data, boq: boq, boq_lines: contract.boq_lines, boq_value: contract.boq_value });
      setEditOpen(false); toast.success("Contract updated"); loadSummary();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update contract"); }
    finally { setBusy(""); }
  };
  const setBuyer = (k, v) => setEditForm((f) => ({ ...f, buyer: { ...f.buyer, [k]: v } }));

  const loadSummary = useCallback(() => {
    api.get(`/einvoice/contracts/${id}/summary`).then((s) => setSummary(s.data)).catch(() => {});
  }, [id]);

  const load = useCallback(() => {
    Promise.all([api.get(`/einvoice/contracts/${id}`), api.get(`/einvoice/contracts/${id}/ipcs`)])
      .then(([c, i]) => { setContract(c.data); setBoq(c.data.boq || []); setIpcs(i.data.ipcs); })
      .catch(() => { toast.error("Contract not found"); navigate("/einvoicing/app"); })
      .finally(() => setLoading(false));
  }, [id, navigate]);
  useEffect(() => { load(); loadSummary(); }, [load, loadSummary]);

  const saveBoq = async (rows) => {
    setBusy("boq");
    try {
      const r = await api.put(`/einvoice/contracts/${id}/boq`, { lines: rows });
      setBoq(r.data.boq); setContract({ ...contract, boq_lines: r.data.lines, boq_value: r.data.value, boq: r.data.boq, boq_rev: r.data.boq_rev });
      toast.success(r.data.boq_rev >= 1 ? `Contract BOQ saved as Rev ${String(r.data.boq_rev).padStart(2, "0")}` : "Contract BOQ saved");
      load(); loadSummary();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save BOQ"); }
    finally { setBusy(""); }
  };

  const uploadBoq = async (file) => {
    if (!file) return;
    setBusy("boq");
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/einvoice/contracts/${id}/boq-upload`, fd);
      setBoq(r.data.boq); toast.success(`Auto-filled ${r.data.lines} lines — review and edit`);
      load(); loadSummary();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(""); if (upRef.current) upRef.current.value = ""; }
  };

  const setRow = (i, k, v) => setBoq(boq.map((r, idx) => {
    if (idx !== i) return r;
    const nr = { ...r, [k]: v };
    if (k === "quantity" || k === "rate") {
      const q = parseFloat(k === "quantity" ? v : nr.quantity) || 0;
      const rt = parseFloat(k === "rate" ? v : nr.rate) || 0;
      nr.scope = Math.round(q * rt * 100) / 100;
    }
    return nr;
  }));
  const addRow = () => setBoq([...boq, { line_no: String(boq.length + 1), description: "", quantity: 0, unit: "", rate: 0, scope: 0 }]);
  const delRow = (i) => setBoq(boq.filter((_, idx) => idx !== i));

  // "Next IPC" helper — suggest the next number and carry forward prior cumulative %
  const carryPct = () => {
    const mx = {};
    ipcs.forEach((p) => (p.progress || []).forEach((x) => { const n = String(x.line_no); mx[n] = Math.max(mx[n] || 0, Number(x.cum_pct) || 0); }));
    return mx;
  };
  const suggestNextNumber = () => {
    let best = 0, prefix = "IPC-", pad = 2;
    ipcs.forEach((p) => {
      const m = String(p.ipc_number || "").match(/^(.*?)(\d+)\s*$/);
      if (m) { const n = parseInt(m[2], 10); if (n >= best) { best = n; prefix = m[1] || "IPC-"; pad = m[2].length; } }
    });
    if (!ipcs.length) return "IPC-01";
    return `${prefix}${String(best + 1).padStart(pad, "0")}`;
  };
  const openNewIpc = (v) => {
    setOpen(v);
    if (v) setIpcForm({ ipc_number: suggestNextNumber(), ipc_type: "progress", ipc_description: "", period_date: "", issue_date: "" });
  };

  const createIpc = async () => {
    if (!ipcForm.ipc_number.trim()) { toast.error("Enter an IPC number"); return; }
    setBusy("ipc");
    try {
      const mx = carryPct();
      const progress = (ipcForm.ipc_type === "progress" || ipcForm.ipc_type === "final")
        ? boq.map((b) => ({ line_no: String(b.line_no), cum_pct: mx[String(b.line_no)] || 0 }))
        : [];
      const r = await api.post(`/einvoice/contracts/${id}/ipcs`, { ...ipcForm, progress, extras: [] });
      setIpcs([r.data, ...ipcs]); setOpen(false);
      setIpcForm({ ipc_number: "", ipc_type: "progress", ipc_description: "", period_date: "", issue_date: "" });
      if (Object.keys(mx).length) toast.message("Carried forward", { description: "Previous cumulative % copied in — enter the new figures." });
      loadSummary();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create IPC"); }
    finally { setBusy(""); }
  };
  const deleteIpc = async (iid) => { await api.delete(`/einvoice/ipcs/${iid}`); setIpcs(ipcs.filter((x) => x.id !== iid)); toast.success("IPC deleted"); loadSummary(); };
  const refreshIpcs = () => { api.get(`/einvoice/contracts/${id}/ipcs`).then((i) => setIpcs(i.data.ipcs)); loadSummary(); };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div data-testid="einvoice-contract-page">
      <button onClick={() => navigate("/einvoicing/app")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-4"><ArrowLeft className="w-4 h-4" /> All contracts</button>

      <div className="rounded-2xl border border-border bg-card p-5 mb-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{contract.title}</h1>
            <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5"><Building2 className="w-4 h-4" /> {contract.buyer?.company_name || "No buyer set"}{contract.buyer?.trn ? ` · TRN ${contract.buyer.trn}` : ""}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="final-account-btn" onClick={() => setFinalOpen(true)}><ClipboardCheck className="w-4 h-4" /> Final account</Button>
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="edit-contract-btn" onClick={() => openEdit(true)}><Pencil className="w-4 h-4" /> Edit</Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
          {contract.contract_ref && <span>Ref: {contract.contract_ref}</span>}
          {contract.contract_date && <span>Contract date: {contract.contract_date}</span>}
          {contract.buyer?.peppol_id && <span>Recipient Peppol ID: {contract.buyer.peppol_id}</span>}
          <span>Retention: {contract.retention_pct}%</span>
          {!!contract.advance_pct && <span>Advance: {contract.advance_pct}%</span>}
        </div>
      </div>

      {/* Contract dashboard */}
      {summary && (
        <div data-testid="contract-dashboard" className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { k: "value", label: "Contract value", icon: Building2, val: `AED ${money(summary.contract_value)}`, sub: `${summary.ipc_count} IPC(s) · ${summary.invoiced_count} invoiced` },
            { k: "certified", label: "Certified to-date", icon: TrendingUp, val: `AED ${money(summary.certified_todate)}`, sub: `${summary.certified_pct}% of contract` },
            { k: "advance", label: "Advance outstanding", icon: Wallet, val: `AED ${money(summary.advance_outstanding)}`, sub: `of AED ${money(summary.advance_total)} paid` },
            { k: "retention", label: "Retention held", icon: Landmark, val: `AED ${money(summary.retention_held)}`, sub: "withheld to-date" },
          ].map((c) => (
            <div key={c.k} data-testid={`dash-${c.k}`} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><c.icon className="w-4 h-4 text-primary" /> {c.label}</div>
              <div className="text-xl font-extrabold text-slate-900 dark:text-white mt-2">{c.val}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{c.sub}</div>
              {c.k === "certified" && (
                <div className="h-1.5 bg-secondary rounded-full mt-2 overflow-hidden"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${Math.min(100, summary.certified_pct)}%` }} /></div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Contract BOQ */}
      <div className="rounded-2xl border border-border bg-card p-5 mb-6">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h2 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" /> Contract BOQ
            {(contract.boq_rev || 0) >= 1 && <span data-testid="boq-rev-badge" className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300">Rev {String(contract.boq_rev).padStart(2, "0")}</span>}
          </h2>
          <div className="flex items-center gap-2">
            {(contract.boq_lines || 0) > 0 && (
              <Button variant="ghost" size="sm" className="gap-1.5 text-xs" data-testid="boq-download-current" onClick={() => downloadFile(`/einvoice/contracts/${id}/boq-pdf`, `BOQ-${contract.contract_ref || "contract"}-rev${String(contract.boq_rev || 0).padStart(2, "0")}.pdf`)}><FileText className="w-3.5 h-3.5" /> PDF</Button>
            )}
            <Button variant="ghost" size="sm" className="gap-1.5 text-xs" data-testid="boq-template-btn" onClick={() => downloadFile("/einvoice/boq-template?kind=contract", "matiqs-contract-boq-template.xlsx")}>Template</Button>
            <input ref={upRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="contract-boq-upload" onChange={(e) => uploadBoq(e.target.files[0])} />
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="boq-upload-btn" disabled={busy === "boq"} onClick={() => upRef.current?.click()}><Upload className="w-3.5 h-3.5" /> Upload & auto-fill</Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Enter Quantity, Unit and Rate per line — the Amount is Quantity × Rate. For a lump-sum scope, use Quantity 1, Unit "Lump Sum" and put the value in Rate. Upload a BOQ workbook to auto-fill.</p>
        {ipcs.length === 0 && (
          <div data-testid="boq-correctness-note" className="flex items-start gap-2 rounded-xl border border-indigo-300 dark:border-indigo-900 bg-indigo-50/70 dark:bg-indigo-950/20 px-3 py-2.5 mb-3 text-xs text-indigo-800 dark:text-indigo-300">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Please double-check this BOQ before you start certifying. Once you issue IPCs, any change to quantities or rates creates a new revision and <strong>affects future IPC calculations</strong> — so getting it right now avoids rework.</span>
          </div>
        )}
        {ipcs.length > 0 && (
          <div data-testid="boq-revision-warning" className="flex items-start gap-2 rounded-xl border border-amber-300 dark:border-amber-900 bg-amber-50/70 dark:bg-amber-950/20 px-3 py-2.5 mb-3 text-xs text-amber-800 dark:text-amber-300">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>This contract already has {ipcs.length} IPC{ipcs.length === 1 ? "" : "s"}. Saving changes creates a <strong>new BOQ revision</strong> — invoices already issued on existing IPCs are <strong>not</strong> affected, and new IPCs will use the revised quantities. You can download any previous revision below.</span>
          </div>
        )}
        <div className="overflow-x-auto border border-border rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-accent text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-bold w-16 whitespace-nowrap">Line #</th>
                <th className="px-3 py-2 text-left font-bold">Description</th>
                <th className="px-3 py-2 text-right font-bold w-24">Qty</th>
                <th className="px-3 py-2 text-left font-bold w-32">Unit</th>
                <th className="px-3 py-2 text-right font-bold w-32">Rate (AED)</th>
                <th className="px-3 py-2 text-right font-bold w-36">Amount (AED)</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {boq.map((row, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-2 py-1"><Input className="h-8" data-testid={`boq-lineno-${i}`} value={row.line_no} onChange={(e) => setRow(i, "line_no", e.target.value)} /></td>
                  <td className="px-2 py-1"><Input className="h-8" data-testid={`boq-desc-${i}`} value={row.description} onChange={(e) => setRow(i, "description", e.target.value)} placeholder="Scope / item" /></td>
                  <td className="px-2 py-1"><Input type="number" className="h-8 text-right" data-testid={`boq-qty-${i}`} value={row.quantity ?? ""} onChange={(e) => setRow(i, "quantity", parseFloat(e.target.value) || 0)} /></td>
                  <td className="px-2 py-1">
                    <select data-testid={`boq-unit-${i}`} value={row.unit || ""} onChange={(e) => setRow(i, "unit", e.target.value)}
                            className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm">
                      <option value="">—</option>
                      {BOQ_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1"><Input type="number" className="h-8 text-right" data-testid={`boq-rate-${i}`} value={row.rate ?? ""} onChange={(e) => setRow(i, "rate", parseFloat(e.target.value) || 0)} /></td>
                  <td className="px-2 py-1 text-right font-semibold tabular-nums" data-testid={`boq-amount-${i}`}>{money(row.scope || 0)}</td>
                  <td className="px-2 py-1 text-center"><button onClick={() => delRow(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(contract.boq_history || []).length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="boq-history">
            <span className="text-xs font-semibold text-muted-foreground">Previous revisions:</span>
            {(contract.boq_history || []).map((h) => (
              <Button key={h.rev} variant="outline" size="sm" className="gap-1.5 h-7 text-xs" data-testid={`boq-history-${h.rev}`}
                      onClick={() => downloadFile(`/einvoice/contracts/${id}/boq-pdf?rev=${h.rev}`, `BOQ-${contract.contract_ref || "contract"}-rev${String(h.rev).padStart(2, "0")}.pdf`)}>
                <FileText className="w-3 h-3" /> {h.rev === 0 ? "Original" : `Rev ${String(h.rev).padStart(2, "0")}`} · {money(h.value)}
              </Button>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between mt-3">
          <Button variant="ghost" size="sm" className="gap-1.5" data-testid="boq-add-row" onClick={addRow}><Plus className="w-4 h-4" /> Add line</Button>
          <Button size="sm" className="gap-1.5" data-testid="boq-save" disabled={busy === "boq"} onClick={() => saveBoq(boq)}>{busy === "boq" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save BOQ</Button>
        </div>
      </div>

      {/* Variation Orders */}
      <div className="rounded-2xl border border-border bg-card p-5 mb-6" data-testid="variations-panel">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2"><TrendingUp className="w-4 h-4 text-brand" /> Variation Orders</h2>
          <Button size="sm" variant="outline" className="gap-1.5" data-testid="add-variation-btn" disabled={(contract.boq_lines || 0) === 0} onClick={() => openVo(null)}><Plus className="w-4 h-4" /> Add variation</Button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Approved variations raise (or reduce) a BOQ line's scope mid-contract. The revised scope flows into every IPC, the over-certification check and the dashboard.</p>
        {variations.length === 0 ? (
          <div data-testid="variations-empty" className="text-sm text-muted-foreground border border-dashed border-border rounded-xl py-6 text-center">No variations yet — the original contract value stands.</div>
        ) : (
          <div className="overflow-x-auto border border-border rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-accent text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-bold w-24">Ref</th>
                  <th className="px-3 py-2 text-left font-bold w-16">Line</th>
                  <th className="px-3 py-2 text-left font-bold">Description</th>
                  <th className="px-3 py-2 text-right font-bold w-40">Amount (AED)</th>
                  <th className="px-3 py-2 text-left font-bold w-28">Date</th>
                  <th className="px-3 py-2 text-center font-bold w-28">Status</th>
                  <th className="px-3 py-2 text-right font-bold w-20"></th>
                </tr>
              </thead>
              <tbody>
                {variations.map((vo) => (
                  <tr key={vo.id} className="border-t border-border" data-testid={`variation-row-${vo.ref || vo.id}`}>
                    <td className="px-3 py-2 font-medium">{vo.ref || "—"}</td>
                    <td className="px-3 py-2">{vo.line_no}</td>
                    <td className="px-3 py-2 text-muted-foreground">{vo.description || "—"}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${Number(vo.amount) < 0 ? "text-destructive" : "text-emerald-600 dark:text-emerald-400"}`}>{Number(vo.amount) >= 0 ? "+" : ""}{money(vo.amount)}</td>
                    <td className="px-3 py-2 text-muted-foreground">{vo.date || "—"}</td>
                    <td className="px-3 py-2 text-center">
                      <button data-testid={`variation-toggle-${vo.ref || vo.id}`} onClick={() => toggleVo(vo)}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${vo.approved !== false ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"}`}>
                        {vo.approved !== false ? <><CheckCircle2 className="w-3 h-3" /> Approved</> : <><AlertTriangle className="w-3 h-3" /> Pending</>}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button className="text-muted-foreground hover:text-foreground mr-2" data-testid={`variation-edit-${vo.ref || vo.id}`} onClick={() => openVo(vo)}><Pencil className="w-3.5 h-3.5" /></button>
                      <button className="text-muted-foreground hover:text-destructive" data-testid={`variation-del-${vo.ref || vo.id}`} onClick={() => delVo(vo.id)}><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {Number(contract.variation_total) !== 0 && (
          <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-1 mt-3 text-sm" data-testid="variations-totals">
            <span className="text-muted-foreground">Original value: <span className="font-medium text-foreground">AED {money(contract.boq_value)}</span></span>
            <span className="text-muted-foreground">Approved variations: <span className={`font-medium ${Number(contract.variation_total) < 0 ? "text-destructive" : "text-emerald-600 dark:text-emerald-400"}`}>{Number(contract.variation_total) >= 0 ? "+" : ""}AED {money(contract.variation_total)}</span></span>
            <span className="font-bold text-foreground">Revised value: AED {money(contract.revised_value)}</span>
          </div>
        )}
      </div>

      <Dialog open={voOpen} onOpenChange={setVoOpen}>
        <DialogContent className="max-w-md" data-testid="variation-dialog">
          <DialogHeader><DialogTitle>{voEditId ? "Edit variation order" : "Add variation order"}</DialogTitle><DialogDescription>Approved amounts revise the chosen BOQ line's scope. Use a negative amount for an omission.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>BOQ line *</Label>
                <Select value={voForm.line_no} onValueChange={(v) => setVoForm({ ...voForm, line_no: v })}>
                  <SelectTrigger data-testid="variation-line-select"><SelectValue placeholder="Select line" /></SelectTrigger>
                  <SelectContent>{boq.map((b) => <SelectItem key={String(b.line_no)} value={String(b.line_no)}>Line {b.line_no} — {b.description || "(no name)"}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>VO reference</Label><Input data-testid="variation-ref-input" value={voForm.ref} onChange={(e) => setVoForm({ ...voForm, ref: e.target.value })} placeholder="VO-01" /></div>
            </div>
            <div><Label>Description</Label><Input data-testid="variation-desc-input" value={voForm.description} onChange={(e) => setVoForm({ ...voForm, description: e.target.value })} placeholder="e.g. Additional basement waterproofing" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount (AED)</Label><Input type="number" data-testid="variation-amount-input" value={voForm.amount} onChange={(e) => setVoForm({ ...voForm, amount: e.target.value })} placeholder="Negative = omission" /></div>
              <div><Label>Approval date</Label><Input type="date" data-testid="variation-date-input" value={voForm.date} onChange={(e) => setVoForm({ ...voForm, date: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input type="checkbox" data-testid="variation-approved-check" className="h-4 w-4 accent-[hsl(22_93%_52%)]" checked={voForm.approved} onChange={(e) => setVoForm({ ...voForm, approved: e.target.checked })} />
              Approved — apply this variation to the line's scope now
            </label>
          </div>
          <DialogFooter><Button data-testid="variation-save" onClick={saveVo} disabled={busy === "vo"} className="gap-1.5">{busy === "vo" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {voEditId ? "Save changes" : "Add variation"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IPCs */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">Interim Payment Certificates</h2>
        <Dialog open={open} onOpenChange={openNewIpc}>
          <DialogTrigger asChild><Button data-testid="new-ipc-btn" className="gap-1.5" disabled={(contract.boq_lines || 0) === 0}><Plus className="w-4 h-4" /> New IPC</Button></DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>New Interim Payment Certificate</DialogTitle><DialogDescription>Number is suggested automatically and previous cumulative % are carried forward. Edit as needed.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>IPC number *</Label><Input data-testid="ipc-number-input" value={ipcForm.ipc_number} onChange={(e) => setIpcForm({ ...ipcForm, ipc_number: e.target.value })} placeholder="IPC-01" /></div>
                <div>
                  <Label>Type</Label>
                  <Select value={ipcForm.ipc_type} onValueChange={(v) => setIpcForm({ ...ipcForm, ipc_type: v })}>
                    <SelectTrigger data-testid="ipc-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{IPC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description</Label><Input data-testid="ipc-description-input" value={ipcForm.ipc_description} onChange={(e) => setIpcForm({ ...ipcForm, ipc_description: e.target.value })} placeholder="e.g. May valuation / Mobilisation advance" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>IPC period date</Label><Input type="date" data-testid="ipc-period-input" value={ipcForm.period_date} onChange={(e) => setIpcForm({ ...ipcForm, period_date: e.target.value })} /></div>
                <div><Label>IPC issue date</Label><Input type="date" data-testid="ipc-issue-input" value={ipcForm.issue_date} onChange={(e) => setIpcForm({ ...ipcForm, issue_date: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button data-testid="create-ipc-submit" onClick={createIpc} disabled={busy === "ipc"} className="gap-1.5">{busy === "ipc" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create IPC</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* IPC register */}
      {ipcs.length > 0 && (
        <div data-testid="ipc-register" className="overflow-x-auto border border-border rounded-2xl mb-4 bg-card">
          <table className="w-full text-xs">
            <thead className="bg-accent text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-3 py-2 text-left font-bold">IPC</th><th className="px-3 py-2 text-left font-bold">Type</th><th className="px-3 py-2 text-left font-bold">Period</th><th className="px-3 py-2 text-left font-bold">Issued</th><th className="px-3 py-2 text-right font-bold">Net (AED)</th><th className="px-3 py-2 text-center font-bold">Status</th></tr>
            </thead>
            <tbody>
              {[...ipcs].reverse().map((p) => {
                const net = p.invoice?.total ?? p.review?.total;
                return (
                  <tr key={p.id} className="border-t border-border" data-testid={`register-row-${p.id}`}>
                    <td className="px-3 py-1.5 font-semibold text-slate-900 dark:text-white">{p.ipc_number}</td>
                    <td className="px-3 py-1.5">{typeLabel(p.ipc_type)}</td>
                    <td className="px-3 py-1.5">{p.period_date || "—"}</td>
                    <td className="px-3 py-1.5">{p.issue_date || "—"}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{net != null ? money(net) : "—"}</td>
                    <td className="px-3 py-1.5 text-center">
                      {p.invoice
                        ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded">Invoiced</span>
                        : <span className="text-[10px] font-bold text-amber-700 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 rounded">Draft</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {ipcs.length === 0 ? (
        <div data-testid="ipcs-empty" className="text-center py-14 border border-dashed border-border rounded-2xl">
          <ReceiptText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">{(contract.boq_lines || 0) === 0 ? "Add the Contract BOQ first, then create IPCs." : "No IPCs yet. Create your first certificate."}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="ipcs-list">
          {ipcs.map((ipc) => <IpcCard key={ipc.id} ipc={ipc} contract={contract} boq={boq} onChanged={refreshIpcs} onDelete={deleteIpc} fullService={fullService} onFixBuyer={() => openEdit(true)} onViewClearance={() => navigate("/einvoicing/clearance")} onCreditNote={(iid) => navigate(`/einvoicing/credit-notes?source_kind=ipc&source_id=${iid}`)} />)}
        </div>
      )}

      {/* Edit contract details */}
      <Dialog open={editOpen} onOpenChange={openEdit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit contract details</DialogTitle><DialogDescription>Change the project, dates, retention/advance and the recipient (buyer) details.</DialogDescription></DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div><Label>Contract title</Label><Input data-testid="edit-title-input" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Project name</Label><Input value={editForm.project_name} onChange={(e) => setEditForm({ ...editForm, project_name: e.target.value })} /></div>
                <div><Label>Contract / PO ref</Label><Input value={editForm.contract_ref} onChange={(e) => setEditForm({ ...editForm, contract_ref: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Contract date</Label><Input type="date" data-testid="edit-contract-date-input" value={editForm.contract_date} onChange={(e) => setEditForm({ ...editForm, contract_date: e.target.value })} /></div>
                <div><Label>Retention %</Label><Input type="number" value={editForm.retention_pct} onChange={(e) => setEditForm({ ...editForm, retention_pct: parseFloat(e.target.value) || 0 })} /></div>
                <div><Label>Advance %</Label><Input type="number" value={editForm.advance_pct} onChange={(e) => setEditForm({ ...editForm, advance_pct: parseFloat(e.target.value) || 0 })} /></div>
              </div>
              <div className="pt-1 border-t border-border">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-3 mb-2">Buyer (billed party)</div>
                <div className="space-y-3">
                  <div><Label>Company name</Label><Input data-testid="edit-buyer-name-input" value={editForm.buyer.company_name} onChange={(e) => setBuyer("company_name", e.target.value)} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Buyer TRN</Label>
                      <Input data-testid="edit-buyer-trn-input" value={editForm.buyer.trn} onChange={(e) => {
                        const v = e.target.value;
                        const prevDerived = peppolFromTrn(editForm.buyer.trn);
                        const keep = editForm.buyer.peppol_id && editForm.buyer.peppol_id !== prevDerived;
                        setEditForm({ ...editForm, buyer: { ...editForm.buyer, trn: v, peppol_id: keep ? editForm.buyer.peppol_id : peppolFromTrn(v) } });
                      }} placeholder="15 digits" />
                      {trnDigits(editForm.buyer.trn).length > 0 && trnDigits(editForm.buyer.trn).length !== 15 && (
                        <p data-testid="edit-buyer-trn-warn" className="text-[11px] text-amber-600 mt-1">TRN must be 15 digits (currently {trnDigits(editForm.buyer.trn).length}).</p>
                      )}
                    </div>
                    <div><Label>City / Emirate</Label><Input value={editForm.buyer.city} onChange={(e) => setBuyer("city", e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Recipient Peppol ID</Label><Input data-testid="edit-buyer-peppol-input" className="font-mono" value={editForm.buyer.peppol_id} onChange={(e) => setBuyer("peppol_id", e.target.value)} placeholder={peppolFromTrn(editForm.buyer.trn) || "0235:XXXXXXXXXX"} /></div>
                    <div><Label>Address</Label><Input data-testid="edit-buyer-address-input" value={editForm.buyer.address} onChange={(e) => setBuyer("address", e.target.value)} /></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="edit-contract-save" onClick={saveContract} disabled={busy === "edit"} className="gap-1.5">{busy === "edit" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Final account reconciliation */}
      <Dialog open={finalOpen} onOpenChange={setFinalOpen}>
        <DialogContent className="max-w-lg" data-testid="final-account-dialog">
          <DialogHeader><DialogTitle>Final account</DialogTitle><DialogDescription>Closing reconciliation across all IPCs on this contract.</DialogDescription></DialogHeader>
          {summary ? (
            <div className="space-y-4 text-sm">
              <FinalGroup title="Work certified" rows={[
                ["Contract value", summary.contract_value],
                ["Certified to-date", summary.certified_todate, `${summary.certified_pct}%`],
                ["Remaining to certify", summary.remaining_to_certify],
              ]} />
              <FinalGroup title="Advance payment" rows={[
                ["Advance paid", summary.advance_total],
                ["Advance recovered", summary.advance_recovered],
                ["Advance outstanding", summary.advance_outstanding, null, true],
              ]} />
              <FinalGroup title="Retention" rows={[
                ["Retention held (gross)", summary.retention_gross],
                ["Retention released", summary.retention_released],
                ["Retention balance", summary.retention_held, null, true],
              ]} />
              <FinalGroup title="Billing" rows={[
                ["Of which VAT (5%)", summary.total_vat],
                [`Total invoiced (${summary.invoiced_count}/${summary.ipc_count} IPC)`, summary.total_invoiced, null, true],
              ]} />
            </div>
          ) : <div className="flex items-center gap-2 text-muted-foreground py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FinalGroup({ title, rows }) {
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <div className="bg-accent px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{title}</div>
      <div className="divide-y divide-border">
        {rows.map(([label, val, note, strong], i) => (
          <div key={i} className={`flex items-center justify-between px-3 py-2 ${strong ? "bg-secondary/40 font-bold text-slate-900 dark:text-white" : ""}`}>
            <span className={strong ? "" : "text-muted-foreground"}>{label}{note ? <span className="text-xs text-primary ml-1.5">{note}</span> : null}</span>
            <span className="font-mono">AED {money(val)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

